// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Stock, Bom } from "../stock/index.js"

// @@imports-module
import { AdhesiveCalculator } from "./Adhesive.js"

// @@imports-types
import {
    Config,
    TapeType,
    PatchSize,
    ADHESIVE_TYPES,
    TAPE_TYPES,
    PATCH_SIZES,
    PATCH_TYPES,
} from "../types/index.js"

// @@body
// Approximate pre-calculated area sqm for primer coverage required to apply an
// external corner patch.
const PATCH_COVERAGE = 0.073

/**
 * @todo Complete method documentation including inline comments, jsdoc
 * annotations, and class description.
 * @todo Check that all error throws use decorated error format.
 * @todo Add tape filter to remove tapes which do not have sufficient quantity
 * to cover requested area.
 */
class TapeCalculator {
    #stock
    #config
    #adhesives
    #bom = new Bom()

    /**
     * 
     * @param {Stock} stock 
     * @param {Config} config 
     * @param {AdhesiveCalculator} adhesiveCalculator
     */
    constructor(stock, config, adhesiveCalculator) {
        this.#stock = stock
        this.#config = config
        this.#adhesives = adhesiveCalculator
    }

    /**
     * 
     * @param {number} length 
     * @param {TapeType} type 
     * @param {object} obj 
     * @param {number} [obj.width]
     * @param {number} [obj.rollWidth]
     */
    addTape(length, type, { width, rollWidth } = {}) {
        rollWidth ??= type === TAPE_TYPES.uncured ? 0.225
            : type === TAPE_TYPES.seam ? 0.075
            : 0.15

        const tape = this.#stock
            .getTapes(type)
            .sort((tapeA, tapeB) => {
                return tapeA.width - tapeB.width ||
                    tapeA.quantity - tapeB.quantity
            })
            .filter(tape => {
                return tape.runningStock * tape.quantity > length
            })
            .find((tape, index, tapes) => {
                // @ts-ignore - Roll width is defined by null assignment, but
                // throws ts error when making declaration file.
                return index === tapes.length - 1 || tape.width >= rollWidth
            })
        if (!tape) {
            throw new Error()
        }

        const {
            tapes: { overlap, primerBorder },
            extraAllowances: { tape: extra }
        } = this.#config

        width ??= tape.width
        let layers = 0
        while (layers < (width + (layers - 1) * overlap) / tape.width) {
            layers++
        }

        let coverage = (width + primerBorder) * (length + primerBorder) +
            (layers - 1) * (overlap + primerBorder) * (length + primerBorder)
        if (type === TAPE_TYPES.uncured) { coverage /= 2 }
        this.#adhesives.addAdhesive(ADHESIVE_TYPES.primer, coverage)
        if (this.#config.tapes.addSealant) {
            this.#adhesives.addAdhesive(
                ADHESIVE_TYPES.sealants.lapEdge,
                width * 2 + length * 2
            )
        }

        const quantity = length * extra * layers / tape.quantity
        this.#bom.addLineItem(tape, quantity)
        tape.runningStock -= quantity
    }

    /**
     * add patch length and patch width enums... add medium as default 
     * @param {PatchSize} patchSize 
     */
    addPatch(patchSize) {
        const {
            tapes: { overlap },
            overhangs: { default: { client: upstand } }
        } = this.#config
        const length = patchSize === PATCH_SIZES.extraSmall ? overlap * 2
            : patchSize === PATCH_SIZES.small ? overlap * 2 + upstand
            : patchSize === PATCH_SIZES.medium ? overlap * 2 + upstand * 2
            : patchSize === PATCH_SIZES.large ? overlap * 2 + upstand * 3
            : overlap * 2 + upstand * 4
        const patchType = patchSize === PATCH_SIZES.extraSmall
            ? PATCH_TYPES.internal
            : PATCH_TYPES.external
        const patch = this.#stock.getTapes(TAPE_TYPES.uncured, { patchType })[0]
        if (!patch) { throw new Error() }

        /**
         * @todo Replace if-else with single call to addTape method, then add
         * precut option to add tape method, and account for precut patches
         * there to that the primer does not need to be hard-coded, and so that
         * the calculator can quote multiple patches for extended upstands.
         */
        if (this.#config.tapes.precutPatches) {
            this.#adhesives.addAdhesive(ADHESIVE_TYPES.primer, PATCH_COVERAGE)
            this.#bom.addLineItem(patch, 1)
        }
        else { this.addTape(length, TAPE_TYPES.uncured) }
    }

    get bom() {
        return this.#bom
    }
}

// @@exports
export { TapeCalculator }
